import React from 'react';
import { AlertCircle, ShoppingBag, Store } from 'lucide-react';
import { formatCurrency } from '../../../lib/formatters';

export interface CartDrawerFooterProps {
  subtotal: number;
  bottleReturnsCount: number;
  bottleReturnsCredit: number;
  uncountedBagAttached?: boolean;
  uniqueStoreCount?: number;
  lineItemCount: number;
  isCheckoutDisabled: boolean;
  checkoutButtonText: string;
  onCheckoutClick: () => void;
  tier?: string;
  requiresAgeVerification?: boolean;
}

const TIER_DISCOUNTS: Record<string, number> = {
  BRONZE: 15,
  SILVER: 25,
  GOLD: 35,
  PLATINUM: 45,
  GREEN: 50
};

const CartDrawerFooter: React.FC<CartDrawerFooterProps> = ({
  subtotal,
  bottleReturnsCount,
  bottleReturnsCredit,
  uncountedBagAttached = false,
  uniqueStoreCount = 1,
  lineItemCount,
  isCheckoutDisabled,
  checkoutButtonText,
  onCheckoutClick,
  tier,
  requiresAgeVerification = false
}) => {
  return (
    <div
      className="p-6 border-t border-white/5 bg-black/30 backdrop-blur-md space-y-4"
      style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 0px))' }}
    >
      <div className="flex items-center justify-between px-2">
        <p className="text-xs font-black uppercase tracking-widest text-slate-400">Subtotal</p>
        <p className="text-xl font-black text-white tabular-nums">{formatCurrency(subtotal)}</p>
      </div>
      {tier && TIER_DISCOUNTS[tier.toUpperCase()] ? (
        <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
          <span className="text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            {tier.toUpperCase()} Member
          </span>
          <span className="text-xs font-bold tabular-nums">
            {TIER_DISCOUNTS[tier.toUpperCase()]}% off delivery at checkout
          </span>
        </div>
      ) : null}
      {bottleReturnsCount > 0 ? (
        <div className="flex items-center justify-between px-2">
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">
            Bottle returns ({bottleReturnsCount})
          </p>
          <p className="text-sm font-black text-ninpo-lime tabular-nums">
            −{formatCurrency(bottleReturnsCredit)} est.
          </p>
        </div>
      ) : null}
      {uncountedBagAttached ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 space-y-1 text-left">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-amber-300">
              <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
              Uncounted Bag Attached
            </span>
            <span className="text-xs font-bold text-amber-200">Driver counts</span>
          </div>
          <p className="text-[10px] text-amber-100/90 leading-relaxed">
            <strong>Fee Notice (!):</strong> A 2¢/container counting fee is deducted upon driver verification (you receive 8¢ net credit per container). To receive the full 10¢ with zero fees, scan your bottles yourself in the cart.
          </p>
        </div>
      ) : null}

      {requiresAgeVerification ? (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 space-y-1 text-left">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-red-300">
              <span className="text-sm">🔞</span>
              18+ Age Verification Required
            </span>
            <span className="text-xs font-bold text-red-300">Photo ID</span>
          </div>
          <p className="text-[10px] text-red-100/90 leading-relaxed">
            Your cart contains age-restricted items. A valid government photo ID must be verified or shown upon delivery (MCL § 333.7410b).
          </p>
        </div>
      ) : null}

      {uniqueStoreCount > 1 ? (
        <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-3 space-y-1 text-left">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-purple-300">
              <Store className="w-4 h-4 text-purple-400 shrink-0" />
              Multi-Store Basket ({uniqueStoreCount} Partner Stores)
            </span>
            <span className="text-xs font-bold text-purple-200">+$1.99 multi-hop</span>
          </div>
          <p className="text-[10px] text-purple-100/90 leading-relaxed">
            Items will be picked across {uniqueStoreCount} local neighborhood stores. Perishables and cold drinks are picked last to preserve fresh cold-chain delivery.
          </p>
        </div>
      ) : null}
      <p className="px-2 text-[10px] text-slate-500 leading-relaxed">
        Delivery fees, sales tax, and bottle deposits are pass-through charges calculated at checkout based on your address.
        Return credit is estimated here and finalized at checkout.
      </p>

      {lineItemCount === 0 && bottleReturnsCount > 0 && bottleReturnsCount < 100 && (
        <div className="flex items-start gap-2.5 px-4 py-3.5 bg-ninpo-red/10 border border-ninpo-red/20 text-ninpo-red rounded-xl text-xs font-bold leading-normal mb-1">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
          <span>Standalone pickups require a minimum of 100 bottle returns (you have {bottleReturnsCount}).</span>
        </div>
      )}
      {lineItemCount === 0 && uncountedBagAttached && bottleReturnsCount < 100 && (
        <div className="flex items-start gap-2.5 px-4 py-3.5 bg-amber-500/10 border border-amber-500/20 text-amber-100 rounded-xl text-xs font-bold leading-normal mb-1">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Add grocery items to checkout — an uncounted bag rides with your delivery so your driver can count it.</span>
        </div>
      )}

      <button
        type="button"
        onClick={onCheckoutClick}
        disabled={isCheckoutDisabled}
        className="w-full py-4 px-3 bg-ninpo-lime text-ninpo-black rounded-xl text-[11px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 transition-transform active:scale-95 duration-200 text-center leading-tight whitespace-normal break-words"
      >
        <ShoppingBag className="w-5 h-5 shrink-0 animate-pulse" />
        <span className="min-w-0">{checkoutButtonText}</span>
      </button>
    </div>
  );
};

export default CartDrawerFooter;
